// General Disclaimer for CareerOS-AI.
import { Link } from "react-router-dom";
import LegalLayout, { bullets } from "./LegalLayout";

const EFFECTIVE = "July 16, 2026";

const SECTIONS = [
  { n: 1, id: "no-professional-advice", title: "Not professional advice",
    body: (<>
      <p>CareerOS-AI provides educational information about careers, salaries, resumes, interviews, finances, taxes, and related topics. It is <strong>not</strong> a substitute for advice from a licensed professional.</p>
      <p>Nothing on CareerOS-AI constitutes:</p>
      {bullets([
        "legal advice",
        "tax or accounting advice",
        "financial, investment, or retirement advice",
        "immigration advice",
        "medical or mental-health advice",
        "employment-law advice",
      ])}
      <p>For decisions that require professional expertise, please consult a qualified adviser in your jurisdiction.</p>
    </>) },
  { n: 2, id: "ai-generated-content", title: "AI-generated content",
    body: (<>
      <p>Much of what you see on CareerOS-AI is generated by AI. AI systems make mistakes. Concretely:</p>
      {bullets([
        "Numbers that change yearly (tax rates, contribution limits, minimum wage, retirement age, filing deadlines) should be verified with the appropriate authority before you act.",
        "AI may generate confident-sounding text that is inaccurate, incomplete, or out of date.",
        "The same prompt may produce different outputs on different runs.",
      ])}
      <p>Read our full <Link to="/ai-policy">AI Usage &amp; Responsible AI Policy</Link>.</p>
    </>) },
  { n: 3, id: "no-guarantees", title: "No guarantees of outcomes",
    body: (<>
      <p>CareerOS-AI is a tool. Your career results depend on many factors we do not control — the labour market, employer decisions, timing, and your own actions.</p>
      <p>We do not guarantee:</p>
      {bullets([
        "employment",
        "job interviews",
        "promotions",
        "salary increases",
        "acceptance into educational programs",
        "any specific financial or career outcome",
      ])}
    </>) },
  { n: 4, id: "third-party-links", title: "Third-party links",
    body: (<>
      <p>CareerOS-AI may link to or integrate with third-party sites, tools, and services (e.g. LinkedIn, job boards, educational platforms, payment processors, model providers). We do not endorse or accept responsibility for third-party content, availability, or practices.</p>
    </>) },
  { n: 5, id: "as-is", title: "Service provided 'as is'",
    body: (<>
      <p>To the maximum extent permitted by law, CareerOS-AI is provided <em>&quot;as is&quot;</em> and <em>&quot;as available&quot;</em>, without warranties of any kind — express, implied, statutory, or otherwise — including fitness for a particular purpose, merchantability, title, or non-infringement.</p>
      <p>See our <Link to="/terms">Terms and Conditions</Link>, section 16, for the full limitation of liability.</p>
    </>) },
  { n: 6, id: "changes", title: "Changes to this disclaimer",
    body: <p>We may update this disclaimer. Material changes are communicated on the website. The &quot;Last Updated&quot; date reflects the current version.</p> },
  { n: 7, id: "contact", title: "Contact",
    body: <p>Questions? Email <span className="font-mono">support@careeros-ai.com</span>.</p> },
];

const INTRO = (
  <p>This Disclaimer supplements our <Link to="/terms">Terms and Conditions</Link>, <Link to="/privacy">Privacy Policy</Link>, and <Link to="/ai-policy">AI Usage Policy</Link>. It states — plainly — what CareerOS-AI is and is not.</p>
);

export default function Disclaimer() {
  return (
    <LegalLayout
      eyebrow="Legal · Disclaimer"
      title="Disclaimer"
      subtitle="for CareerOS-AI"
      effectiveDate={EFFECTIVE}
      lastUpdated={EFFECTIVE}
      intro={INTRO}
      sections={SECTIONS}
      currentPath="/disclaimer"
    />
  );
}
